import { useAuthenticatedApi } from "@x-veloper/dataslot-auth";
import type {
  ProductionLogResponse,
  ProductionLogQuery,
  PerformanceResponse,
  PerformanceQuery,
  BaselineResponse,
  MachineActivityResponse,
  MachineDetailResponse,
  PeopleActivityResponse,
  PersonDetailResponse,
} from "@/shared/types/production";

export class ProductionService {
  constructor(private api: ReturnType<typeof useAuthenticatedApi>) {}

  async getLog(query: ProductionLogQuery): Promise<ProductionLogResponse> {
    const params = new URLSearchParams({
      fromMs: String(query.fromMs),
      toMs: String(query.toMs),
    });
    const response = await this.api.request(`/api/production/log?${params}`);
    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }
    const data: ProductionLogResponse = await response.json();
    if (!data.success) {
      throw new Error(data.error || "Failed to fetch production log");
    }
    return data;
  }

  async getPerformance(query: PerformanceQuery): Promise<PerformanceResponse> {
    const params = new URLSearchParams({
      fromMs: String(query.fromMs),
      toMs: String(query.toMs),
    });
    const response = await this.api.request(`/api/production/performance?${params}`);
    return response.json();
  }

  async getBaseline(days: number = 30): Promise<BaselineResponse> {
    const params = new URLSearchParams({ days: String(days) });
    const response = await this.api.request(`/api/production/baseline?${params}`);
    return response.json();
  }

  async getMachineActivity(days: number = 14): Promise<MachineActivityResponse> {
    const params = new URLSearchParams({ days: String(days) });
    const response = await this.api.request(`/api/production/machines?${params}`);
    return response.json();
  }

  async getMachineDetail(machine: string, days: number = 30): Promise<MachineDetailResponse> {
    const params = new URLSearchParams({ days: String(days) });
    const response = await this.api.request(`/api/production/machines/${encodeURIComponent(machine)}?${params}`);
    return response.json();
  }

  async getPeopleActivity(days: number = 14): Promise<PeopleActivityResponse> {
    const params = new URLSearchParams({ days: String(days) });
    const response = await this.api.request(`/api/production/people?${params}`);
    return response.json();
  }

  async getPersonDetail(employeeId: string, days: number = 30): Promise<PersonDetailResponse> {
    const params = new URLSearchParams({ days: String(days) });
    const response = await this.api.request(`/api/production/people/${encodeURIComponent(employeeId)}?${params}`);
    return response.json();
  }
}

export function useProductionService(): ProductionService {
  const api = useAuthenticatedApi();
  return new ProductionService(api);
}
